import React, { useState, useEffect, useRef } from 'react';
import CaHeader from '../components/CaHeader';
import CaFooter from '../components/CaFooter';
import CaWhoWeAre from '../components/CaWhoWeAre';
import CaOnlineCourses from '../components/CaOnlineCourses';
import CaFreeAssessment from '../components/CaFreeAssessment';
import CaWhyChooseUs from '../components/CaWhyChooseUs';
import CaHowItWorks from '../components/CaHowItWorks';
import CaDetailedCourses from '../components/CaDetailedCourses';
import CaKidsClasses from '../components/CaKidsClasses';
import CaPerksBenefits from '../components/CaPerksBenefits';
import CaAboutFeatures from '../components/CaAboutFeatures';
import CaTestimonials from '../components/CaTestimonials';
import CaAdultClasses from '../components/CaAdultClasses';
import CaContactSection from '../components/CaContactSection';
import FreeTrialModal from '../components/FreeTrialModal';
import { submitForm } from '../utils/formSubmit';
import Swal from 'sweetalert2';
import { FiArrowRight } from 'react-icons/fi';
import './Ca.css';
import CertifiedTutors from '../components/CertifiedTutors';
import PricingAndStates from '../components/PricingAndStates';
import FAQSection from '../components/FAQSection';

// Embedded inline SVG Canada Flag
const CaFlag = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 900 450"
    style={{ borderRadius: '2px', display: 'inline-block', verticalAlign: 'middle', width: '22px', height: '14px', flexShrink: 0 }}
  >
    <rect width="900" height="450" fill="#fff" />
    <rect width="225" height="450" fill="#D80621" />
    <rect x="675" width="225" height="450" fill="#D80621" />
    <polygon fill="#D80621" points="450,75 420,165 330,165 400,215 375,305 450,255 525,305 500,215 570,165 480,165" />
  </svg>
);

const countryList = [
  {
    name: 'Canada',
    code: '+1',
    iso: 'ca'
  }
];

const Ca = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    course: '',
    phone: '',
    message: ''
  });

  const [selectedCountry, setSelectedCountry] = useState(countryList[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitForm('Ca Page Hero Form', {
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        email: 'Not Provided (Hero Form)',
        phone: `${selectedCountry.code} ${formData.phone}`,
        course: formData.course,
        message: formData.message
      });
      Swal.fire({
        title: 'Trial Booked!',
        text: `Thank you, ${formData.firstName}! Your 3-Day Free Trial request has been submitted.`,
        icon: 'success',
        confirmButtonColor: '#006100'
      });
      setFormData({
        firstName: '',
        lastName: '',
        course: '',
        phone: '',
        message: ''
      });
      window.location.href = '/thank-you-enrollment-successful/';
    } catch (error) {
      Swal.fire({
        title: 'Submission Error',
        text: 'Failed to submit form. Please try again.',
        icon: 'error',
        confirmButtonColor: '#006100'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCountries = countryList.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.code.includes(searchQuery)
  );

  return (
    <div className="ca-page">
      {/* Custom Header */}
      <CaHeader onOpenModal={() => setIsModalOpen(true)} />

      {/* Main Content Area */}
      <main className="ca-main-content">
        {/* Hero Section */}
        <section
          className="ca-hero"
          style={{
            backgroundImage: `linear-gradient(rgba(75, 143, 68, 0.88), rgba(75, 143, 68, 0.88)), url('${process.env.PUBLIC_URL}/assets/images/Hero-5.jpg')`
          }}
        >
          <div className="ca-hero__overlay"></div>
          <div className="ca-hero__container">
            {/* Left Content Column */}
            <div className="ca-hero__left">
              <div className="ca-hero__guarantee-badge">
                <svg className="ca-hero__badge-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-3zm-1 14.5l-3.5-3.5 1.41-1.41L11 13.67l5.09-5.09 1.41 1.41L11 16.5z" />
                </svg>
                <span>100% Satisfaction Guarantee</span>
              </div>

              <h1 className="ca-hero__title">
                Online Quran Classes for Kids &amp; Adults in Canada – Live, One-on-One &amp; Certified
              </h1>

              <p className="ca-hero__description">
                Al Quran Lesson connects Muslim families across Canada with certified, native Arabic-speaking tutors for structured, one-on-one online Quran classes for kids and adults on your schedule, with zero compromise on quality. Trusted since 2013. 5,000+ students enrolled. Book your 3-day free trial now without providing a card and start learning the Quran at your ease.
              </p>

              <div className="ca-hero__cta">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="ca-hero__cta-btn"
                  style={{ border: 'none', cursor: 'pointer' }}
                >
                  Claim Your Free Trial <FiArrowRight className="ca-hero__cta-arrow" />
                </button>
              </div>
            </div>

            {/* Right Form Card Column */}
            <div className="ca-hero__right" id="hero-form">
              <div className="ca-hero__form-card">
                <h2 className="ca-hero__form-header">
                  No Credit Card Required · 3-Day Free Trial · Cancel Anytime
                </h2>

                <form onSubmit={handleSubmit} className="ca-hero__form">
                  <div className="ca-hero__form-row">
                    <input
                      type="text"
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="ca-hero__input"
                    />
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="ca-hero__input"
                    />
                  </div>

                  <div className="ca-hero__form-group">
                    <select
                      name="course"
                      value={formData.course}
                      onChange={handleChange}
                      required
                      className="ca-hero__select"
                    >
                      <option value="" disabled hidden>Select Courses</option>
                      <option value="Noorani Qaida">Noorani Qaida</option>
                      <option value="Quran Tajweed Classes">Quran Tajweed Classes</option>
                      <option value="Quran Reading Classes">Quran Reading Classes</option>
                      <option value="Quran Memorization Classes">Quran Memorization Classes</option>
                      <option value="Quran Translation Classes">Quran Translation Classes</option>
                    </select>
                  </div>

                  {/* Interactive Phone Field with Canada Flag SVG */}
                  <div className="ca-hero__form-group" ref={dropdownRef} style={{ position: 'relative' }}>
                    <div className="ca-hero__phone-box">
                      <button
                        type="button"
                        className="ca-hero__country-picker-btn"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      >
                        <span className="ca-hero__country-flag">
                          <CaFlag />
                        </span>
                        <span className="ca-hero__country-arrow">▲</span>
                        <span className="ca-hero__country-code">{selectedCountry.code}</span>
                      </button>
                      <span className="ca-hero__phone-divider">|</span>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="ca-hero__phone-field"
                      />
                    </div>

                    {/* Country Code Dropdown */}
                    {isDropdownOpen && (
                      <div className="ca-hero__country-dropdown">
                        <div className="ca-hero__search-wrapper">
                          <input
                            type="text"
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="ca-hero__search-input"
                            autoFocus
                          />
                        </div>
                        <ul className="ca-hero__country-menu">
                          {filteredCountries.map((c) => (
                            <li
                              key={c.name}
                              className={`ca-hero__country-option ${c.name === selectedCountry.name ? 'ca-hero__country-option--active' : ''}`}
                              onClick={() => {
                                setSelectedCountry(c);
                                setIsDropdownOpen(false);
                                setSearchQuery('');
                              }}
                            >
                              <span className="ca-hero__opt-flag">
                                <CaFlag />
                              </span>
                              <span className="ca-hero__opt-name">{c.name}</span>
                              <span className="ca-hero__opt-code">{c.code}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="ca-hero__form-group">
                    <input
                      type="text"
                      name="message"
                      placeholder="Message"
                      value={formData.message}
                      onChange={handleChange}
                      className="ca-hero__input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="ca-hero__submit-btn"
                  >
                    {submitting ? 'Booking Trial...' : 'Book My Free Trial'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Card Overlapping Banner */}
        <section className="ca-stats-banner">
          <div className="ca-stats-banner__container">
            <div className="ca-stats-banner__card">
              <div className="ca-stats-banner__grid">
                {/* Stat Item 1 */}
                <div className="ca-stat-box">
                  <div className="ca-stat-box__number">20+</div>
                  <div className="ca-stat-box__hearts">
                    ❤️ ❤️ ❤️ ❤️
                  </div>
                  <div className="ca-stat-box__label">Certified Tutors</div>
                </div>

                {/* Stat Item 2 */}
                <div className="ca-stat-box">
                  <div className="ca-stat-box__number">4.6</div>
                  <div className="ca-stat-box__stars">
                    ⭐ ⭐ ⭐ ⭐ ⭐
                  </div>
                  <div className="ca-stat-box__label">Reviews</div>
                </div>

                {/* Stat Right Message */}
                <h3 className="ca-stats-banner__text">
                  Trusted by <strong>5,000+ happy students</strong> that are joining with us to achieve their goal!
                </h3>
              </div>
            </div>
          </div>
        </section>

        {/* Ca Dedicated Who We Are Section (with rotating certified stamp on scroll) */}
        <CaWhoWeAre onOpenModal={() => setIsModalOpen(true)} />

        {/* Ca Online Quran Courses Section matching screenshots */}
        <CaOnlineCourses />

        {/* Ca Free Registration Assessment Section matching screenshot */}
        <CaFreeAssessment />

        {/* Ca Why Choose Us Section matching screenshot */}
        <CaWhyChooseUs />

        {/* Ca How It Works Section matching screenshot */}
        <CaHowItWorks />

        {/* Ca Detailed Quran Courses Section matching screenshot & user data */}
        <CaDetailedCourses />

        {/* Ca Kids Quran Classes Section matching screenshot */}
        <CaKidsClasses onOpenModal={() => setIsModalOpen(true)} />

        {/* Ca Perks and Benefits Section matching screenshot */}
        <CaPerksBenefits />

        {/* Ca About Features Section matching screenshot (6 features + Arch image + CTA) */}
        <CaAboutFeatures onOpenModal={() => setIsModalOpen(true)} />

        {/* Ca Testimonials Section matching screenshot (Their Stories Speak For Us) */}
        <CaTestimonials />

        {/* Ca Online Quran Classes For Adults Section matching screenshot (No internal links) */}
        <CaAdultClasses onOpenModal={() => setIsModalOpen(true)} />


      </main>

      <CertifiedTutors useContactLink={true} />
      <PricingAndStates defaultCurrency="CAD" onOpenModal={() => setIsModalOpen(true)} />
      <FAQSection title="Frequently Asked Questions About Our Online Quran Classes in Canada" />

      {/* Ca Contact Section matching screenshot (Let's Talk About Your Quran Classes Online) */}
      <CaContactSection />

      {/* Custom Footer */}
      <CaFooter />

      {/* Free Trial Modal matching screenshot */}
      <FreeTrialModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default Ca;
